import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { ForgotPasswordModel } from 'services/EpdClient';
import AuthService from 'services/AuthService';
import { toast } from 'react-toastify';
import { Link, RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { PrimaryButton } from 'components/v2/buttons';
import InputFormField from 'components/v2/form/InputFormField';
import EpdRoutes from 'routes/EpdRoutes';
import AuthLayoutFrame from 'components/auth/AuthLayoutFrame';
import AuthTitle from 'components/auth/AuthTitle';
import { HyperLink } from 'styles/Styles.styled';

const ForgotPassword: React.FC<RouteComponentProps> = ({ history }) => {
  const initialValues: ForgotPasswordModel = {
    email: '',
  };

  const forgotPasswordSchema = Yup.object({
    email: Yup.string().required('Required').email('Invalid email address'),
  });

  const handleSubmit = async (values: ForgotPasswordModel) => {
    try {
      await AuthService.forgotPassword(values);
      history.push(EpdRoutes.account.forgotPasswordSuccess);
    } catch (error: any) {
      const serverError = error?.response
        ? JSON.parse(error.response)
        : { detail: 'Operation was not successful due to an error.' };
      const errorString = serverError.detail || 'Operation was not successful due to an error.';
      toast.error(errorString, {
        position: 'top-center',
      });
    }
  };

  return (
    <AuthLayoutFrame>
      <BodyContainer>
        <AuthTitle>Forgot your password?</AuthTitle>
        <Instructions>
          Enter the email address you used to register.
          <br />
          We will send you instructions to reset your password.
        </Instructions>
        <Formik
          initialValues={initialValues}
          validationSchema={forgotPasswordSchema}
          onSubmit={handleSubmit}
          validateOnMount
        >
          {({ handleSubmit, isSubmitting, isValid }) => (
            <FormWrapper onSubmit={handleSubmit}>
              <InputFormField
                label="Email"
                name="email"
                type="email"
                placeholder="email@example.com"
                errorPositioning="absolute"
              />

              <SubmitButton type="submit" disabled={isSubmitting || !isValid}>
                {isSubmitting ? 'Sending...' : 'Send'}
              </SubmitButton>

              <HyperLink as={Link} to={EpdRoutes.login}>
                Back to login page
              </HyperLink>
            </FormWrapper>
          )}
        </Formik>
      </BodyContainer>
    </AuthLayoutFrame>
  );
};

export default ForgotPassword;

const BodyContainer = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 40px;
  padding-bottom: 60px;
  align-self: center;
  text-align: center;

  ${(props) => props.theme.media.tablet} {
    padding-left: 80px;
    padding-right: 80px;
  }

  ${(props) => props.theme.media.desktop} {
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Instructions = styled.div`
  margin-top: 30px;
  font-size: 16px;
  line-height: 24px;
`;

const FormWrapper = styled.form`
  display: grid;
  gap: 40px;
  margin-top: 60px;
  font-size: 14px;
`;

const SubmitButton = styled(PrimaryButton)`
  justify-content: center;
`;
